import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { Turnstile, turnstileActive } from './Turnstile';
import { FormError, sendCallRequest } from '../lib/forms';

const CONTACT_EMAIL = 'info@willow.tech';

/** The Custom plan in a line. willow.tech/pricing says the same, word for word. */
export const CUSTOM_PLAN_TEXT =
  'For high volume. Per-request pricing, invoiced monthly, higher rate limits.';

/**
 * The Custom plan: an unlisted plan we attach to an account by hand after a
 * conversation (the first is Oku's: no fee, per request, invoiced monthly).
 * There's nothing to buy here, so the card opens a form that reaches us.
 *
 * `email` prefills the form; `account` names the account in the message, so
 * whoever answers knows which one to put the plan on.
 */
export function CustomPlanCard({ email, account }: { email: string; account: string }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  return (
    <div className="rpc-plan rpc-plan--custom" data-testid="plan-custom">
      <div className="rpc-plan-custom-text">
        <div className="rpc-plan-name">Custom</div>
        <p>{CUSTOM_PLAN_TEXT}</p>
      </div>
      <div className="rpc-plan-actions">
        <button
          className="btn btn-sm"
          type="button"
          onClick={() => setOpen(true)}
          data-testid="custom-talk"
        >
          Talk to us
        </button>
      </div>
      {/* In a portal: inside the card, the dialog would light the card's hover border. */}
      {open && createPortal(<TalkToUsModal email={email} account={account} onClose={close} />, document.body)}
    </div>
  );
}

/** The form itself. `account` names the signed-in account in the message; the
 *  pricing page is public, so someone without one leaves it out. */
export function TalkToUsModal({
  email: accountEmail,
  account,
  onClose,
}: {
  email: string;
  account?: string;
  onClose: () => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState(accountEmail);
  const [company, setCompany] = useState('');
  const [volume, setVolume] = useState('');
  const [message, setMessage] = useState('');
  const [honeypot, setHoneypot] = useState('');
  const [token, setToken] = useState('');
  const [captchaKey, setCaptchaKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const downOnBackdrop = useRef(false);

  // Close on Escape and lock the page's scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    // saas takes one message; the volume and the account ride at its top.
    const head = ['Custom plan, from the dashboard.'];
    if (account) head.push(`Account: ${account}`);
    if (volume.trim()) head.push(`Expected volume: ${volume.trim()}`);
    const text = [head.join('\n'), message.trim()].filter(Boolean).join('\n\n');
    setBusy(true);
    setError(null);
    try {
      await sendCallRequest({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        company: company.trim(),
        message: text,
        website: honeypot,
        ...(token ? { turnstile_token: token } : {}),
      });
      setSentTo(email.trim());
    } catch (err) {
      // Turnstile tokens are single-use; reissue a fresh one for the retry.
      setToken('');
      setCaptchaKey((k) => k + 1);
      setError(err instanceof FormError ? err.message : 'Something went wrong. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="modal-overlay"
      // Only a press that starts on the backdrop closes: selecting text in a
      // field and letting go outside the dialog must not throw the form away.
      onMouseDown={(e) => {
        downOnBackdrop.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        if (downOnBackdrop.current && e.target === e.currentTarget) onClose();
      }}
      data-testid="custom-modal-overlay"
    >
      <div
        className="modal custom-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="custom-modal-title"
        data-testid="custom-modal"
      >
        <div className="modal-header">
          <h3 id="custom-modal-title">Talk to us</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        {sentTo != null ? (
          <>
            <div className="form-success custom-success" role="status" data-testid="custom-success">
              <p>
                <strong>Thanks, we got it.</strong>
              </p>
              <p>We'll reply to {sentTo}.</p>
            </div>
            <button className="btn btn-sm" type="button" onClick={onClose} data-testid="custom-done">
              Close
            </button>
          </>
        ) : (
          <>
            <p className="modal-subtitle">Tell us what you need and we'll reply by email.</p>
            <form className="custom-form" onSubmit={onSubmit} data-testid="custom-form">
              {/* People never see this field; bots that fill every input do. */}
              <div className="form-honeypot" aria-hidden="true">
                <label>
                  Website
                  <input
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </label>
              </div>
              <div className="custom-form-row">
                <label className="rpc-field">
                  <span>First name</span>
                  <input
                    autoFocus
                    required
                    maxLength={100}
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    disabled={busy}
                    data-testid="custom-first-name"
                  />
                </label>
                <label className="rpc-field">
                  <span>Last name</span>
                  <input
                    required
                    maxLength={100}
                    autoComplete="family-name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    disabled={busy}
                    data-testid="custom-last-name"
                  />
                </label>
              </div>
              <label className="rpc-field">
                <span>Email</span>
                <input
                  type="email"
                  inputMode="email"
                  required
                  maxLength={254}
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={busy}
                  data-testid="custom-email"
                />
              </label>
              <label className="rpc-field">
                <span>Company</span>
                <input
                  required
                  maxLength={200}
                  autoComplete="organization"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                  disabled={busy}
                  data-testid="custom-company"
                />
              </label>
              <label className="rpc-field">
                <span>
                  Expected volume <em>Optional</em>
                </span>
                <input
                  maxLength={200}
                  placeholder="e.g. 500M requests a month"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  disabled={busy}
                  data-testid="custom-volume"
                />
              </label>
              <label className="rpc-field">
                <span>
                  Message <em>Optional</em>
                </span>
                <textarea
                  rows={4}
                  maxLength={4000}
                  placeholder="What you're building, the chains you need, anything else"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={busy}
                  data-testid="custom-message"
                />
              </label>
              <Turnstile key={captchaKey} onVerify={setToken} />
              {error && (
                <p className="form-error" role="alert" data-testid="custom-error">
                  {error} You can also email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
                </p>
              )}
              <button
                className="btn btn-primary custom-form-submit"
                type="submit"
                disabled={busy || (turnstileActive && !token)}
                data-testid="custom-send"
              >
                {busy ? 'Sending…' : 'Send'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
